import React, { Suspense, lazy } from 'react';
import '@/App.css';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from '@/components/ui/sonner';
import { AuthProvider, useAuth } from '@/lib/auth';

import HomePage from '@/pages/public/HomePage';
import BandsListPage from '@/pages/public/BandsListPage';
import BandDetailPage from '@/pages/public/BandDetailPage';
import CalendarPage from '@/pages/public/CalendarPage';
import StageDetailPage from '@/pages/public/StageDetailPage';
import ResultsPage from '@/pages/public/ResultsPage';
import RegulationPage from '@/pages/public/RegulationPage';
import VotePage from '@/pages/public/VotePage';
import RepechagePage from '@/pages/public/RepechagePage';
import RankingEliminatoriasPage from '@/pages/public/RankingEliminatoriasPage';

import AdminLogin from '@/pages/admin/AdminLogin';
import AdminLayout from '@/pages/admin/AdminLayout';
import AdminDashboard from '@/pages/admin/AdminDashboard';
import AdminBands from '@/pages/admin/AdminBands';
import AdminStages from '@/pages/admin/AdminStages';
import AdminStageDetail from '@/pages/admin/AdminStageDetail';
import AdminCommands from '@/pages/admin/AdminCommands';
import AdminSettings from '@/pages/admin/AdminSettings';
import AdminLogs from '@/pages/admin/AdminLogs';
import AdminRepechage from '@/pages/admin/AdminRepechage';
import ChangePassword from '@/pages/admin/ChangePassword';

function RequireAdmin({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-muted-foreground">Carregando…</div>;
  if (!user) return <Navigate to="/admin/login" replace />;
  if (user.must_change_password) return <Navigate to="/admin/trocar-senha" replace />;
  return children;
}

function App() {
  return (
    <div className="App min-h-screen bg-background text-foreground">
      <AuthProvider>
        <BrowserRouter>
          <Suspense fallback={<div className="p-8 text-muted-foreground">Carregando…</div>}>
            <Routes>
              {/* Public routes */}
              <Route path="/" element={<HomePage />} />
              <Route path="/bandas" element={<BandsListPage />} />
              <Route path="/bandas/:slug" element={<BandDetailPage />} />
              <Route path="/calendario" element={<CalendarPage />} />
              <Route path="/etapas/:slug" element={<StageDetailPage />} />
              <Route path="/resultados" element={<ResultsPage />} />
              <Route path="/resultados/repescagem" element={<RepechagePage />} />
              <Route path="/ranking-eliminatorias" element={<RankingEliminatoriasPage />} />
              <Route path="/regulamento" element={<RegulationPage />} />
              <Route path="/votar/:token" element={<VotePage />} />

              {/* Admin routes */}
              <Route path="/admin/login" element={<AdminLogin />} />
              <Route path="/admin/trocar-senha" element={<ChangePassword />} />
              <Route
                path="/admin"
                element={
                  <RequireAdmin>
                    <AdminLayout />
                  </RequireAdmin>
                }
              >
                <Route index element={<Navigate to="/admin/dashboard" replace />} />
                <Route path="dashboard" element={<AdminDashboard />} />
                <Route path="bandas" element={<AdminBands />} />
                <Route path="etapas" element={<AdminStages />} />
                <Route path="etapas/:id" element={<AdminStageDetail />} />
                <Route path="etapas/:id/comandas" element={<AdminCommands />} />
                <Route path="repescagem" element={<AdminRepechage />} />
                <Route path="configuracoes" element={<AdminSettings />} />
                <Route path="logs" element={<AdminLogs />} />
              </Route>

              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </Suspense>
          <Toaster richColors position="top-center" theme="dark" />
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}

export default App;
